/**
 * lib/views/javascripts/proto/voice/audio.js — the live-voice client's pure
 * audio helpers: 16-bit PCM to and from base64 for the realtime socket, a
 * frame's peak, the cheap linear resample to 24 kHz, and a WAV from captured
 * frames for the composed engine's transcription.
 *
 * Contents: view
 * Ontology: Code object M33C-1035 (immutable ref — assigned once, never edit)
 * Team: J. Brant Arseneau & the Mach33 Agents.
 */

// ── Constants ────────────────────────────────────────────────────────────
// Vega's tuned values, earned in production (ADR 0013): keep them exactly.
export const SAMPLE_RATE = 24000;

// ── PCM over the socket ──────────────────────────────────────────────────
export function int16ToBase64(buf) {
  const u8 = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}

export function base64ToInt16(b64) {
  const bin = atob(b64), n = bin.length & ~1, u8 = new Uint8Array(n);
  for (let i = 0; i < n; i++) u8[i] = bin.charCodeAt(i);
  return new Int16Array(u8.buffer);
}

// ── Frames ───────────────────────────────────────────────────────────────
export function peak(i16) {
  let p = 0;
  for (let i = 0; i < i16.length; i++) { const a = Math.abs(i16[i]); if (a > p) p = a; }
  return p;
}

// Cheap linear resample if the audio context refused 24 kHz.
export function resampleTo24k(i16, fromRate) {
  if (fromRate === SAMPLE_RATE) return i16;
  const ratio = fromRate / SAMPLE_RATE, out = new Int16Array(Math.floor(i16.length / ratio));
  for (let i = 0; i < out.length; i++) out[i] = i16[Math.floor(i * ratio)];
  return out;
}

// ── WAV ──────────────────────────────────────────────────────────────────
// 16-bit mono PCM at SAMPLE_RATE, little-endian, with the 44-byte header.
export function wavFromFrames(frames) {
  let n = 0;
  for (let i = 0; i < frames.length; i++) n += frames[i].length;
  const buf = new ArrayBuffer(44 + n * 2), v = new DataView(buf);
  const ws = (off, s) => { for (let k = 0; k < s.length; k++) v.setUint8(off + k, s.charCodeAt(k)); };
  ws(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); ws(8, 'WAVE'); ws(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, SAMPLE_RATE, true); v.setUint32(28, SAMPLE_RATE * 2, true);
  v.setUint16(32, 2, true); v.setUint16(34, 16, true); ws(36, 'data'); v.setUint32(40, n * 2, true);
  let off = 44;
  for (let i = 0; i < frames.length; i++) { const f = frames[i]; for (let j = 0; j < f.length; j++) { v.setInt16(off, f[j], true); off += 2; } }
  return new Blob([buf], { type: 'audio/wav' });
}
